import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Paper,
  Stack,
  useTheme,
  Tooltip,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  QrCode as QrCodeIcon,
  Apple as AppleIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useLoading } from '../../contexts/LoadingContext';
import { useTranslation } from 'react-i18next';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import apiService from '../../services/apiService';
import AuthBackground from '../../components/Auth/AuthBackground';
import SocialProfileCompletionModal from '../../components/Auth/SocialProfileCompletionModal';



const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { login, googleLogin, appleLogin, setAuthSession } = useAuth();
  const { setLoading } = useLoading();
  const theme = useTheme();

  const [loginMode, setLoginMode] = useState<'options' | 'email' | 'qr'>('options');
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchor(event.currentTarget);
  };
  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };
  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    handleLanguageMenuClose();
  };

  const [qrSessionId, setQrSessionId] = useState<string | null>(null);
  const [qrStatus, setQrStatus] = useState<'PENDING' | 'AUTHORIZED' | 'EXPIRED'>('PENDING');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [pendingUser, setPendingUser] = useState<any | null>(null);

  // Handle success message from phone verification or other redirects
  React.useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Initialize Apple Sign In
  React.useEffect(() => {
    if ((window as any).AppleID) {
      (window as any).AppleID.auth.init({
        clientId: process.env.REACT_APP_APPLE_CLIENT_ID || 'com.rideclubnet.web',
        scope: 'name email',
        redirectURI: 'https://rideclubnet.com/login',
        usePopup: true,
      });
    }
  }, []);

  // QR Session Polling
  React.useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    if (loginMode === 'qr' && qrSessionId && qrStatus === 'PENDING') {
      pollInterval = setInterval(async () => {
        try {
          const response = await apiService.pollQRSession(qrSessionId);
          if (response.success && response.data) {
            if (response.data.status === 'AUTHORIZED' && response.data.token && response.data.user) {
              setQrStatus('AUTHORIZED');
              setAuthSession(response.data.token, response.data.user);
              navigate('/');
            }
          }
        } catch (error: any) {
          if (error.response?.status === 410 || error.response?.status === 404) {
            setQrStatus('EXPIRED');
          }
          console.error('QR Polling error:', error);
        }
      }, 3000);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [loginMode, qrSessionId, qrStatus, setAuthSession, navigate]);

  const handleStartQR = async () => {
    setError('');
    setLoginMode('qr');
    setQrStatus('PENDING');
    try {
      const response = await apiService.createQRSession();
      if (response.success && response.data) {
        setQrSessionId(response.data.sessionId);
      }
    } catch (err: any) {
      setError(t('loginPage.qrError'));
      setLoginMode('options');
    }
  };

  const validateForm = (): boolean => {
    if (!formData.email.trim()) {
      setError(t('loginPage.emailRequired'));
      return false;
    }
    if (!formData.email.includes('@')) {
      setError(t('loginPage.emailInvalid'));
      return false;
    }
    if (!formData.password) {
      setError(t('loginPage.passwordRequired'));
      return false;
    }
    if (formData.password.length < 6) {
      setError(t('loginPage.passwordLength'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsSubmitting(true);
    setLoading(true, t('loginPage.signingIn'));

    try {
      await login(formData.email.toLowerCase().trim(), formData.password);

      const searchParams = new URLSearchParams(location.search);
      const redirectPath = searchParams.get('redirect');

      if (redirectPath) {
        navigate(decodeURIComponent(redirectPath));
      } else {
        navigate('/');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || t('loginPage.invalidCredentials'));
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (response.credential) {
      setLoading(true, t('loginPage.signingInGoogle') || 'Signing in with Google...');
      try {
        const result = await googleLogin(response.credential);
        if (result.success) {
          if (result.isNewUser) {
            setPendingToken(result.pendingToken || null);
            setPendingUser(result.pendingUser || null);
            setShowProfileCompletion(true);
          } else {
            const searchParams = new URLSearchParams(location.search);
            const redirectPath = searchParams.get('redirect');
            if (redirectPath) {
              navigate(decodeURIComponent(redirectPath));
            } else {
              navigate('/');
            }
          }
        } else {
          setError(result.message || t('loginPage.googleLoginFailed') || 'Google login failed');
        }
      } catch (err: any) {
        setError(t('loginPage.googleLoginFailed') || 'Google login failed');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleGoogleError = () => {
    setError(t('loginPage.googleLoginFailed') || 'Google login failed');
  };

  const handleAppleLogin = async () => {
    try {
      if (!(window as any).AppleID) {
        setError(t('loginPage.appleSdkNotLoaded') || 'Apple Sign In is not available at the moment.');
        return;
      }

      const response = await (window as any).AppleID.auth.signIn();
      if (response.authorization) {
        const { id_token } = response.authorization;
        const appleUser = response.user;

        setLoading(true, t('loginPage.signingInApple') || 'Signing in with Apple...');
        const result = await appleLogin(id_token, appleUser ? {
          givenName: appleUser.name.firstName,
          familyName: appleUser.name.lastName
        } : undefined);

        if (result.success) {
          if (result.isNewUser) {
            setPendingToken(result.pendingToken || null);
            setPendingUser(result.pendingUser || null);
            setShowProfileCompletion(true);
          } else {
            navigate('/');
          }
        } else {
          setError(result.message || 'Apple login failed');
        }
      }
    } catch (err: any) {
      if (err.error !== 'popup_closed_by_user') {
        console.error('Apple Login error:', err);
        setError(t('loginPage.appleLoginFailed') || 'Apple login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthBackground>
      <Box sx={{ width: '100%', maxWidth: 480, position: 'relative' }}>

        <Box sx={{ position: 'absolute', top: -48, right: 0, zIndex: 10 }}>
          <Tooltip title={t('navbar.language', 'Language')}>
            <IconButton
              onClick={handleLanguageMenuOpen}
              sx={{
                color: theme.palette.text.secondary,
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '20px',
                px: 1.5,
                py: 0.5,
                gap: 1,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                }
              }}
            >
              <LanguageIcon sx={{ fontSize: '1.25rem' }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {i18n.language.startsWith('en') ? 'EN' : 
                 i18n.language.startsWith('fr') ? 'FR' : 
                 i18n.language.startsWith('es') ? 'ES' : 
                 i18n.language.startsWith('fa') ? 'FA' : 
                 i18n.language.startsWith('ar') ? 'AR' :
                 i18n.language.startsWith('zh-CN') ? '简' :
                 i18n.language.startsWith('zh-TW') ? '繁' :
                 i18n.language.startsWith('pa') ? 'PA' :
                 i18n.language.startsWith('hi') ? 'HI' : 'EN'}
              </Typography>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={languageMenuAnchor}
            open={Boolean(languageMenuAnchor)}
            onClose={handleLanguageMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 120,
                borderRadius: 2,
                boxShadow: theme.shadows[4],
              }
            }}
          >
            <MenuItem onClick={() => changeLanguage('en')} selected={i18n.language.startsWith('en')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                EN
              </Box> English
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('fr')} selected={i18n.language.startsWith('fr')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                FR
              </Box> Français
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('es')} selected={i18n.language.startsWith('es')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ES
              </Box> Español
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('fa')} selected={i18n.language.startsWith('fa')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                FA
              </Box> فارسی
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('ar')} selected={i18n.language.startsWith('ar')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                AR
              </Box> العربية
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('zh-CN')} selected={i18n.language.startsWith('zh-CN')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                简
              </Box> 简体中文
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('zh-TW')} selected={i18n.language.startsWith('zh-TW')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                繁
              </Box> 繁體中文
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('pa')} selected={i18n.language.startsWith('pa')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                PA
              </Box> ਪੰਜਾਬੀ
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('hi')} selected={i18n.language.startsWith('hi')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                HI
              </Box> हिन्दी
            </MenuItem>
          </Menu>
        </Box>

        {/* Main App Logo Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, letterSpacing: '-0.02em', fontSize: '2rem' }}>
            {t('loginPage.welcome') || 'Welcome Back'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
            {t('loginPage.signInJourney') || 'Sign in to return to the club'}
          </Typography>
        </Box>

        {/* Success / Error Alerts */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>{successMessage}</Alert>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 4, borderRadius: 2 }}
            action={
              error.includes('verify your account') ? (
                <Button color="inherit" size="small" onClick={async () => {
                  try {
                    setLoading(true, t('loginPage.sendingVerification') || 'Sending verification email...');
                    const response = await apiService.post('/auth/resend-email-verification', { email: formData.email });
                    if (response.success) {
                      setSuccessMessage(t('loginPage.verificationSentDesc') || 'A new verification link has been sent to your email.');
                      setError('');
                    } else {
                      setError(response.message || 'Failed to send verification email.');
                    }
                  } catch (err: any) {
                    setError(err.response?.data?.message || 'Failed to send verification email.');
                  } finally {
                    setLoading(false);
                  }
                }}>
                  {t('loginPage.resendVerification') || 'Resend'}
                </Button>
              ) : null
            }
          >
            {error}
          </Alert>
        )}

        {/* VIEWS */}

        {/* 1. INITIAL OPTIONS VIEW */}
        {loginMode === 'options' && (
          <Stack spacing={2} sx={{ mb: 4, alignItems: 'center', width: '100%' }}>
            <Button
              variant="contained"
              startIcon={<AppleIcon sx={{ fontSize: '1.5rem !important', color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' }} />}
              sx={{
                width: '70%',
                bgcolor: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                borderRadius: '8px',
                py: 1.2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: theme.shadows[2],
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#222222', boxShadow: theme.shadows[4] }
              }}
              onClick={handleAppleLogin}
            >
              {t('loginPage.continueWithApple', 'Continue with Apple')}
            </Button>

            <Box sx={{ position: 'relative', width: '70%' }}>
              {/* Hidden Google SDK button */}
              <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, zIndex: 1, overflow: 'hidden' }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  shape="rectangular"
                  size="large"
                  text="continue_with"
                  width={340} // 70% of 480 (maxWidth) is approx 336
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                  color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                  borderRadius: '8px',
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: theme.shadows[2],
                  '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#222222' },
                  pointerEvents: 'none'
                }}
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" style={{ width: 18, height: 18, marginRight: 10 }} />
                {t('loginPage.continueWithGoogle', 'Continue with Google')}
              </Button>
            </Box>

            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => setLoginMode('email')}
              sx={{
                width: '70%',
                color: theme.palette.text.primary,
                borderColor: theme.palette.divider,
                borderRadius: '8px',
                py: 1.2,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { borderColor: theme.palette.text.primary, bgcolor: theme.palette.action.hover }
              }}
            >
              {t('loginPage.continueWithEmail', 'Continue with Email')}
            </Button>

            <Button
              variant="outlined"
              startIcon={<QrCodeIcon />}
              onClick={handleStartQR}
              sx={{
                width: '70%',
                color: theme.palette.text.primary,
                borderColor: theme.palette.divider,
                borderRadius: '8px',
                py: 1.2,
                fontWeight: 600,
                textTransform: 'none',
                opacity: 1, // QR is always fully active and colored
                '&:hover': { borderColor: theme.palette.text.primary, bgcolor: theme.palette.action.hover }
              }}
            >
              {t('loginPage.loginWithQR')}
            </Button>

            {/* Passive Consent Notice */}
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ fontSize: '0.85rem', mt: 2, px: 4, lineHeight: 1.5 }}
            >
              {t('loginPage.passiveConsent', "By signing in, you agree to Ride Club's")}{' '}
              <Link
                component={RouterLink}
                to="/help#terms"
                sx={{ color: theme.palette.text.primary, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {t('common.terms', 'Terms of Service')}
              </Link>{' '}
              {t('common.and', 'and')}{' '}
              <Link
                component={RouterLink}
                to="/help#privacy"
                sx={{ color: theme.palette.text.primary, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {t('common.privacy', 'Privacy Policy')}
              </Link>
              {'.'}
            </Typography>
          </Stack>
        )}

        {/* 2. EMAIL FORM VIEW */}
        {loginMode === 'email' && (
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', ml: 1, fontWeight: 600, letterSpacing: 1 }}>
                {t('loginPage.emailLabel')}
              </Typography>
              <TextField
                fullWidth
                id="email"
                type="email"
                placeholder="name@example.com"
                value={formData.email}
                onChange={handleInputChange('email')}
                autoComplete="email"
                autoFocus
                required
                InputProps={{
                  sx: { fontSize: '1.2rem', padding: '8px 14px' }
                }}
              />
            </Box>

            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', ml: 1, fontWeight: 600, letterSpacing: 1 }}>
                {t('loginPage.passwordLabel')}
              </Typography>
              <TextField
                fullWidth
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={formData.password}
                onChange={handleInputChange('password')}
                autoComplete="current-password"
                required
                InputProps={{
                  sx: { fontSize: '1.2rem', padding: '8px 14px' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Link
                component={RouterLink}
                to="/forgot-password"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  '&:hover': { color: theme.palette.text.primary }
                }}
              >
                {t('loginPage.forgotPassword')}
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                marginTop: 1,
                borderRadius: '8px',
                py: 1.2,
                fontWeight: 600,
                opacity: isSubmitting ? 0.5 : 1,
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#222222' }
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('loginPage.signIn').toUpperCase()
              )}
            </Button>

            <Button
              fullWidth
              onClick={() => setLoginMode('options')}
              sx={{ color: 'text.secondary', fontWeight: 500, mt: 1 }}
            >
              {t('loginPage.alternativeOptions', 'Alternative options')}
            </Button>
          </Box>
        )}

        {/* 3. QR LOGIN VIEW */}
        {loginMode === 'qr' && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
              {t('loginPage.scanTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {t('loginPage.scanDesc')}
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                display: 'inline-block',
                borderRadius: 4,
                mb: 4,
                bgcolor: 'white' // Keep white for contrast with QR scanner
              }}
            >
              {qrStatus === 'PENDING' && qrSessionId ? (
                <QRCodeSVG
                  value={`rideclub://authorize?sessionId=${qrSessionId}`}
                  size={256}
                  level="H"
                  includeMargin={true}
                  imageSettings={{
                    src: '/logo192.png',
                    x: undefined,
                    y: undefined,
                    height: 52,
                    width: 52,
                    excavate: true,
                  }}
                />
              ) : qrStatus === 'EXPIRED' ? (
                <Box sx={{ py: 4, px: 2 }}>
                  <Typography color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
                    {t('loginPage.qrExpired')}
                  </Typography>
                  <Button variant="outlined" sx={{ borderColor: '#000', color: '#000' }} onClick={handleStartQR}>
                    {t('loginPage.refreshQR')}
                  </Button>
                </Box>
              ) : (
                <CircularProgress size={64} sx={{ color: '#000' }} />
              )}
            </Paper>

            <Box>
              <Button
                onClick={() => setLoginMode('options')}
                sx={{ color: 'text.secondary', fontWeight: 500 }}
              >
                {t('loginPage.alternativeOptions') || 'Alternative options'}
              </Button>
            </Box>
          </Box>
        )}


        {/* Sticky Footer */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
            {t('loginPage.noAccount') || "Don't have an account?"}{' '}
            <Link
              component={RouterLink}
              to="/register"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {t('loginPage.signUp') || 'Sign Up'}
            </Link>
          </Typography>
        </Box>

      </Box>

      {/* Social Profile Completion Modal — shown to brand-new Google/Apple users */}
      <SocialProfileCompletionModal
        open={showProfileCompletion}
        onComplete={(updatedUser) => {
          setShowProfileCompletion(false);
          if (pendingToken) {
            setAuthSession(pendingToken, updatedUser);
          }
          const searchParams = new URLSearchParams(location.search);
          const redirectPath = searchParams.get('redirect');
          if (redirectPath) {
            navigate(decodeURIComponent(redirectPath));
          } else {
            navigate('/');
          }
        }}
      />

    </AuthBackground>
  );
};

export default LoginPage;